/**
 * Whether a run's chart version goes with the installer that will run it
 * (IM-196).
 *
 * Qrvey's release pipeline builds the `qrvey-terraform` installer image and the
 * chart from **one version** — installer `qrvey-terraform:9.6-20261007-3555`
 * ships with chart `9.6-20261007-3555` — and a chart from another release line
 * is probably incompatible with that installer. Nothing joined the two here:
 * the chart is typed per run, while the installer is whatever the region's
 * dispatcher stack runs (`RunnerImage`), so a 9.5 chart on a 9.6 installer went
 * ahead and failed somewhere deep in an apply.
 *
 * ## It warns and never blocks
 *
 * They differ on purpose sometimes: a hotfix may ship for one side only, case
 * by case (Amit, 2026-10-09). So a mismatch is something to know before the
 * click, never a refusal — the visible-versus-blocking split `canRun` draws.
 *
 * ## It compares, it does not order
 *
 * "Behind" or "ahead" would be a guess printed as a fact — IM-123's reasoning
 * for the image drift warning. Two builds of one release line are told apart
 * from two release lines, and that is all.
 *
 * One function, read by the preflight, the run form and the instance screen,
 * so the three cannot word the same comparison differently.
 */
import { isRunnerImageAddress } from '../bootstrap/runner-image.js';
/**
 * A version's release line: its leading `major.minor`.
 *
 * `9.6-20261007-3555` and `9.6.0-20261007` are both `9.6`; `9.5-r-20260831-0793`
 * is `9.5`. The boundary after the minor must be `.`, `-` or the end, so `9.60`
 * is not read as `9.6`. Anything else has no line, and is compared as unknown
 * rather than guessed at.
 */
export const releaseLine = (version) => {
    const match = /^(\d+)\.(\d+)(?=[.-]|$)/.exec(version.trim());
    return match === null ? undefined : `${match[1] ?? ''}.${match[2] ?? ''}`;
};
/** The tag of an installer image address, or undefined when it is not one. */
export const installerTag = (image) => isRunnerImageAddress(image) ? image.slice(image.lastIndexOf(':') + 1) : undefined;
/**
 * Compares a chart version with the installer image that would run it.
 *
 * `installerImage` is the full address, as the dispatcher stack holds it.
 * Undefined means it could not be read; a value that is not an address is
 * reported as such, since IM-124 already treats it as a run that cannot start.
 */
export const chartInstallerMatch = (chartVersion, installerImage) => {
    if (installerImage === undefined)
        return { kind: 'unknown', reason: 'no-installer' };
    const tag = installerTag(installerImage);
    if (tag === undefined)
        return { kind: 'unknown', reason: 'not-an-address' };
    const chart = chartVersion.trim();
    if (chart === tag)
        return { kind: 'same', tag };
    const chartLine = releaseLine(chart);
    const installerLine = releaseLine(tag);
    if (chartLine === undefined || installerLine === undefined) {
        return { kind: 'unknown', reason: 'not-comparable', tag };
    }
    return chartLine === installerLine
        ? { kind: 'same-line', tag, line: chartLine }
        : { kind: 'different-line', tag, chartLine, installerLine };
};
export const installerMatchMessage = (chartVersion, match, region) => {
    const chart = chartVersion.trim();
    switch (match.kind) {
        case 'same':
            return { level: 'none', text: `Chart and installer in ${region} are both ${match.tag}.` };
        case 'same-line':
            return {
                level: 'note',
                text: `The installer in ${region} is ${match.tag} and the chart ${chart}: the same ${match.line} release, different builds. Fine for a hotfix to one side; otherwise use the same version for both.`,
            };
        case 'different-line':
            return {
                level: 'warning',
                text: `Chart ${chart} on installer ${match.tag} in ${region}: the two are released together, and a ${match.chartLine} chart is probably incompatible with a ${match.installerLine} installer.`,
            };
        case 'unknown':
            switch (match.reason) {
                case 'no-installer':
                    return {
                        level: 'unknown',
                        text: `The installer image in ${region} could not be read, so whether chart ${chart} goes with it is unknown.`,
                    };
                case 'not-an-address':
                    return {
                        level: 'unknown',
                        text: `${region}'s installer image is not a registry/repository:tag address, so chart ${chart} cannot be compared with it.`,
                    };
                case 'not-comparable':
                    return {
                        level: 'unknown',
                        text: `Chart ${chart} and installer ${match.tag ?? ''} in ${region} do not both start with a release such as 9.6, so they cannot be compared.`,
                    };
            }
    }
};
//# sourceMappingURL=installer-match.js.map